import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type {
  ExperimentDocument,
  ExperimentInputPayload,
  ExperimentSegmentSplit,
  ExperimentType,
  FrictionClass,
} from '../experimentTypes';
import {
  FRICTION_CLASSES,
  REQUIRED_FRICTION_OUTCOMES,
} from '../experimentTypes';
import { EXPERIMENTS_TEST_IDS } from '../experimentsTestIds';
import { FeasibilityPanel } from './FeasibilityPanel';

/** One selectable A/B test (Iteration D AC-1108): a real ab_test placement. */
export interface AbTestOption {
  id: string;
  name: string;
  alias?: string;
}

interface HypothesisFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: ExperimentInputPayload) => Promise<void>;
  /** Enables the J1 advisory feasibility panel (web_ab only) when present. */
  projectId?: string;
  /** When set, the form edits an existing draft experiment. */
  initial?: ExperimentDocument | null;
  /** Review-first input assembled from immutable Creative Decisions refs. It
   * pre-fills a new draft but does not persist anything until submit. */
  initialDraft?: ExperimentInputPayload | null;
  /** Trait key → values, for discovery segment split pre-registration. */
  userTraitOptions?: Record<string, string[]> | null;
  /**
   * Project A/B tests for the picker (AC-1108). Three distinct states:
   *   undefined — caller does not supply a list at all
   *   null      — list is genuinely unavailable (still loading, or load failed)
   *   array     — list loaded; an EMPTY array is a valid loaded state
   * Only the first two degrade to the legacy free-text id field. A loaded-empty
   * project still gets the picker (with just "Not linked yet"), because
   * "this project has no A/B tests" and "we could not read the list" are
   * different facts and must not render identically.
   */
  abTestOptions?: AbTestOption[] | null;
}

const TYPE_LABEL: Record<ExperimentType, string> = {
  web_ab: 'Web A/B test',
  pricing: 'Pricing / paywall test',
  discovery: 'Discovery (audience learning)',
  emulation: 'Emulation run (learning atom, CLI-managed)',
  analyst: 'Analyst review (agent-created)',
  research: 'Research report (agent-created)',
};

/** Kinds an operator can create by hand — analyst/research cards are agent-created only. */
const CREATABLE_TYPES = (Object.keys(TYPE_LABEL) as ExperimentType[]).filter(
  (type) => type !== 'analyst' && type !== 'research',
);

const PRIMARY_METRIC_OPTIONS = [
  'purchase_conversion_rate',
  'revenue_per_assigned_user',
  'profit_per_assigned_user',
  'first_to_second_rate',
];

const GUARDRAIL_OPTIONS = [
  'first_to_second_rate',
  'paywall_reach_rate',
  'refund_rate',
  'dispute_rate',
  'effective_cancel_rate',
  'payment_recovery_rate',
  'complaint_rate',
];

const FRICTION_CLASS_LABELS: Record<FrictionClass, string> = {
  accidental: 'Accidental friction',
  trust: 'Trust-building friction',
  qualification: 'Qualification friction',
  protective: 'Protective friction',
  dark_sludge: 'Dark sludge / harmful friction',
};

/** Positive integers only; anything else means "not pre-registered". */
function parseMinimumSampleSize(raw: string): number | undefined {
  const value = Number.parseInt(raw, 10);
  return Number.isFinite(value) && value > 0 ? value : undefined;
}

export const HypothesisForm: React.FC<HypothesisFormProps> = ({
  open,
  onClose,
  onSubmit,
  projectId,
  initial,
  initialDraft,
  userTraitOptions,
  abTestOptions,
}) => {
  const [type, setType] = useState<ExperimentType>('web_ab');
  const [name, setName] = useState('');
  const [statement, setStatement] = useState('');
  const [primaryMetric, setPrimaryMetric] = useState('');
  const [expectedEffect, setExpectedEffect] = useState('');
  const [minimumSampleSize, setMinimumSampleSize] = useState('');
  const [successCriteria, setSuccessCriteria] = useState('');
  const [guardrails, setGuardrails] = useState<string[]>([]);
  const [abTestId, setAbTestId] = useState('');
  const [placementId, setPlacementId] = useState('');
  const [segmentSplits, setSegmentSplits] = useState<ExperimentSegmentSplit[]>([]);
  const [frictionClass, setFrictionClass] = useState<FrictionClass | ''>('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    const seed = initial ?? initialDraft;
    if (seed) {
      setType(seed.type);
      setName(seed.name);
      setStatement(seed.hypothesis?.statement ?? '');
      setPrimaryMetric(seed.hypothesis?.primaryMetric ?? '');
      setExpectedEffect(seed.hypothesis?.expectedEffect ?? '');
      setMinimumSampleSize(
        seed.hypothesis?.minimumSampleSize ? String(seed.hypothesis.minimumSampleSize) : '',
      );
      setSuccessCriteria(seed.hypothesis?.successCriteria ?? '');
      setGuardrails(seed.hypothesis?.guardrails ?? []);
      setAbTestId(seed.abTestRef?.abTestId ?? '');
      setPlacementId(seed.abTestRef?.placementId ?? '');
      setSegmentSplits(seed.segmentSplits ?? []);
      setFrictionClass(seed.frictionClass ?? '');
    } else {
      setType('web_ab');
      setName('');
      setStatement('');
      setPrimaryMetric('');
      setExpectedEffect('');
      setMinimumSampleSize('');
      setSuccessCriteria('');
      setGuardrails([]);
      setAbTestId('');
      setPlacementId('');
      setSegmentSplits([]);
      setFrictionClass('');
    }
  }, [open, initial, initialDraft]);

  const traitKeys = Object.keys(userTraitOptions ?? {}).sort((a, b) => a.localeCompare(b));
  const creativeHandoff = initialDraft?.creativeDecisionHandoff ?? initial?.creativeDecisionHandoff;

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload: ExperimentInputPayload = {
        type: creativeHandoff ? 'web_ab' : type,
        name: name.trim(),
        hypothesis: {
          statement: statement.trim() || undefined,
          primaryMetric: (creativeHandoff?.primaryMetric ?? primaryMetric) || undefined,
          expectedEffect: expectedEffect.trim() || undefined,
          minimumSampleSize: parseMinimumSampleSize(minimumSampleSize),
          successCriteria: successCriteria.trim() || undefined,
          guardrails: creativeHandoff
            ? [...creativeHandoff.guardrails]
            : guardrails.length ? guardrails : undefined,
        },
        ...(!creativeHandoff && type === 'web_ab' && abTestId.trim() && {
          abTestRef: { abTestId: abTestId.trim(), placementId: placementId.trim() },
        }),
        ...(type === 'discovery' && segmentSplits.length && { segmentSplits }),
        ...(!creativeHandoff && frictionClass
          ? {
              frictionClass,
              frictionOutcomeMetricKeys: [...REQUIRED_FRICTION_OUTCOMES],
            }
          : initial?.frictionClass
            ? { frictionClass: null }
            : {}),
        ...(initialDraft?.creativeDecisionHandoff && {
          creativeDecisionHandoff: initialDraft.creativeDecisionHandoff,
        }),
      };
      await onSubmit(payload);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save experiment');
    } finally {
      setSaving(false);
    }
  };

  const updateSplit = (index: number, patch: Partial<ExperimentSegmentSplit>) => {
    setSegmentSplits(current => current.map((split, i) => (i === index ? { ...split, ...patch } : split)));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth data-testid={EXPERIMENTS_TEST_IDS.formDialog}>
      <DialogTitle>{initial ? 'Edit experiment' : initialDraft ? 'Review experiment draft' : 'New experiment'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
          {creativeHandoff && (
            <Alert severity="info">
              {`Source: ${providerLabel(creativeHandoff.provider)} · ${creativeHandoff.providerResourceName} · decision ${creativeHandoff.decision}. Review this source-bound pre-registration before saving. Source evidence refs remain immutable lineage; this action does not change the provider ad or spend.`}
            </Alert>
          )}
          {creativeHandoff && (
            <Alert severity="info" data-testid={EXPERIMENTS_TEST_IDS.formCreativeHandoffProtocol}>
              <Typography variant="subtitle2">Immutable registered protocol</Typography>
              <Typography variant="body2">Intent: {creativeHandoff.handoffIntent.replace('_', ' ')}</Typography>
              <Typography variant="body2">Estimand: {creativeHandoff.targetEstimand}</Typography>
              <Typography variant="body2">Primary metric: {creativeHandoff.primaryMetric}</Typography>
              <Typography variant="body2">MDE: {creativeHandoff.minimumDetectableEffect}</Typography>
              <Typography variant="body2">Minimum duration: {creativeHandoff.minimumDurationDays} days</Typography>
              <Typography variant="body2">Stopping policy: {creativeHandoff.stoppingPolicy}</Typography>
              <Typography variant="body2">Fixed: {creativeHandoff.fixedFactors.join(' · ')}</Typography>
              <Typography variant="body2">Vary: {creativeHandoff.factorsToVary.join(' · ')}</Typography>
              <Typography variant="body2">
                Early surrogate: {creativeHandoff.surrogate.status}
                {creativeHandoff.surrogate.metricDefinitionRef
                  ? ` · ${creativeHandoff.surrogate.metricDefinitionRef}` : ''}
              </Typography>
            </Alert>
          )}
          <TextField
            label="Name"
            size="small"
            value={name}
            onChange={(event) => setName(event.target.value)}
            inputProps={{ 'data-testid': EXPERIMENTS_TEST_IDS.formName }}
          />
          <FormControl size="small" disabled={Boolean(creativeHandoff)}>
            <InputLabel>Type</InputLabel>
            <Select
              label="Type"
              value={type}
              data-testid={EXPERIMENTS_TEST_IDS.formType}
              onChange={(event) => {
                const nextType = event.target.value as ExperimentType;
                setType(nextType);
                if (nextType === 'emulation') setFrictionClass('');
              }}
            >
              {CREATABLE_TYPES.map(option => (
                <MenuItem key={option} value={option}>{TYPE_LABEL[option]}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {type !== 'emulation' && !creativeHandoff && (
            <>
              <FormControl size="small">
                <InputLabel id="experiment-friction-class-label">Friction class (optional)</InputLabel>
                <Select
                  id="experiment-friction-class"
                  labelId="experiment-friction-class-label"
                  label="Friction class (optional)"
                  value={frictionClass}
                  onChange={(event) => setFrictionClass(event.target.value as FrictionClass | '')}
                >
                  <MenuItem value=""><em>Not a friction hypothesis</em></MenuItem>
                  {FRICTION_CLASSES.map(option => (
                    <MenuItem key={option} value={option}>{FRICTION_CLASS_LABELS[option]}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {frictionClass && (
                <Alert severity="info">
                  Long-term decision outcomes are locked for this friction hypothesis: activation,
                  paid conversion, renewal, refund, chargeback, and contribution value. Step conversion
                  may be monitored, but it cannot decide the experiment alone.
                </Alert>
              )}
            </>
          )}

          <Typography variant="subtitle2">Hypothesis (pre-registration)</Typography>
          <TextField
            label="Hypothesis statement"
            size="small"
            multiline
            minRows={2}
            value={statement}
            onChange={(event) => setStatement(event.target.value)}
            inputProps={{ 'data-testid': EXPERIMENTS_TEST_IDS.formStatement }}
            helperText="What do you believe and why? Required before the experiment can run."
          />
          {creativeHandoff ? (
            <TextField
              label="Primary metric (registered)"
              size="small"
              value={creativeHandoff.primaryMetric}
              disabled
              inputProps={{ 'data-testid': EXPERIMENTS_TEST_IDS.formPrimaryMetric }}
            />
          ) : (
            <FormControl size="small">
              <InputLabel>Primary metric</InputLabel>
              <Select
                label="Primary metric"
                value={primaryMetric}
                data-testid={EXPERIMENTS_TEST_IDS.formPrimaryMetric}
                onChange={(event) => setPrimaryMetric(event.target.value)}
              >
                {PRIMARY_METRIC_OPTIONS.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            label="Expected effect"
            size="small"
            placeholder="+10% purchase conversion"
            value={expectedEffect}
            onChange={(event) => setExpectedEffect(event.target.value)}
            inputProps={{ 'data-testid': EXPERIMENTS_TEST_IDS.formExpectedEffect }}
          />
          <TextField
            label="Minimum sample per variant"
            size="small"
            type="number"
            placeholder="500"
            value={minimumSampleSize}
            onChange={(event) => setMinimumSampleSize(event.target.value)}
            inputProps={{ 'data-testid': EXPERIMENTS_TEST_IDS.formMinimumSample, min: 1, step: 1 }}
            helperText="Peeking guard: results stay 'directional' until every variant reaches this many assigned users."
          />
          {/* J1 — advisory MDE/feasibility planner (never blocks submission). */}
          {type === 'web_ab' && projectId && !creativeHandoff && (
            <FeasibilityPanel
              projectId={projectId}
              scope={(() => {
                const alias = abTestOptions?.find(option => option.id === abTestId)?.alias;
                return alias ? { alias } : undefined;
              })()}
              onUseMinimumSample={(sample) => setMinimumSampleSize(String(sample))}
            />
          )}
          <TextField
            label="Success criteria"
            size="small"
            placeholder="P(beat control) ≥ 95% and expected loss < $0.05/user"
            value={successCriteria}
            disabled={Boolean(creativeHandoff)}
            onChange={(event) => setSuccessCriteria(event.target.value)}
            inputProps={{ 'data-testid': EXPERIMENTS_TEST_IDS.formSuccessCriteria }}
          />
          <FormControl size="small" disabled={Boolean(creativeHandoff)}>
            <InputLabel>Guardrail metrics</InputLabel>
            <Select
              multiple
              label="Guardrail metrics"
              value={guardrails}
              data-testid={EXPERIMENTS_TEST_IDS.formGuardrails}
              onChange={(event) => {
                const next = event.target.value;
                setGuardrails(typeof next === 'string' ? next.split(',') : next);
              }}
              renderValue={(selected) => (
                <Stack direction="row" spacing={0.25} flexWrap="wrap" useFlexGap>
                  {selected.map(item => <Chip key={item} label={item} size="small" />)}
                </Stack>
              )}
            >
              {GUARDRAIL_OPTIONS.map(option => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {type === 'web_ab' && !creativeHandoff && (
            <>
              <Typography variant="subtitle2">Linked A/B test</Typography>
              {/* Iteration D (AC-1108): picker over the project's real A/B
                  tests, names-first. Free-text id stays only as the fallback
                  when the placement list is unavailable — NOT when it is
                  merely empty. Truthiness is the whole test here: [] is
                  truthy, so a loaded-but-empty project renders the picker,
                  while null/undefined (loading or failed) render the field. */}
              {abTestOptions ? (
                <FormControl size="small">
                  <InputLabel>A/B test</InputLabel>
                  <Select
                    label="A/B test"
                    value={abTestId}
                    data-testid={EXPERIMENTS_TEST_IDS.formAbTestId}
                    onChange={(event) => setAbTestId(event.target.value)}
                  >
                    <MenuItem value=""><em>Not linked yet</em></MenuItem>
                    {abTestId && !abTestOptions.some(option => option.id === abTestId) && (
                      <MenuItem value={abTestId}>{abTestId} (not found in this project)</MenuItem>
                    )}
                    {abTestOptions.map(option => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.name}{option.alias ? ` (${option.alias})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  label="A/B test id"
                  size="small"
                  value={abTestId}
                  onChange={(event) => setAbTestId(event.target.value)}
                  inputProps={{ 'data-testid': EXPERIMENTS_TEST_IDS.formAbTestIdFallback }}
                  helperText="Could not load this project's A/B tests — paste the test id manually."
                />
              )}
            </>
          )}

          {type === 'discovery' && (
            <Stack spacing={1} data-testid={EXPERIMENTS_TEST_IDS.formSegmentSplits}>
              <Typography variant="subtitle2">Pre-registered segment splits</Typography>
              {segmentSplits.map((split, index) => (
                <Stack key={index} direction="row" spacing={1} alignItems="center">
                  <FormControl size="small" sx={{ minWidth: 130 }}>
                    <InputLabel>Trait</InputLabel>
                    <Select
                      label="Trait"
                      value={split.traitKey}
                      onChange={(event) => updateSplit(index, { traitKey: event.target.value, values: [] })}
                    >
                      {traitKeys.map(key => <MenuItem key={key} value={key}>{key}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <InputLabel>Values</InputLabel>
                    <Select
                      multiple
                      label="Values"
                      disabled={!split.traitKey}
                      value={split.values}
                      onChange={(event) => {
                        const next = event.target.value;
                        updateSplit(index, { values: typeof next === 'string' ? next.split(',') : next });
                      }}
                    >
                      {(userTraitOptions?.[split.traitKey] ?? []).map(option => (
                        <MenuItem key={option} value={option}>{option}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button size="small" onClick={() => setSegmentSplits(current => current.filter((_, i) => i !== index))}>
                    Remove
                  </Button>
                </Stack>
              ))}
              <Button size="small" onClick={() => setSegmentSplits(current => [...current, { traitKey: '', values: [] }])}>
                Add segment split
              </Button>
            </Stack>
          )}

          {error && <Alert severity="error" data-testid={EXPERIMENTS_TEST_IDS.formError}>{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!name.trim() || saving}
          data-testid={EXPERIMENTS_TEST_IDS.formSave}
          onClick={handleSubmit}
        >
          {initial ? 'Save changes' : initialDraft ? 'Create reviewed draft' : 'Create hypothesis'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function providerLabel(provider: 'meta' | 'google_ads' | 'tiktok_ads'): string {
  return provider === 'meta' ? 'Meta Ads'
    : provider === 'google_ads' ? 'Google Ads'
      : 'TikTok Ads';
}
